import { Block, Blocks } from 'factories/block';
import {
  Assessments,
  Assessment,
  QuestionData,
  State,
  AssessmentData,
} from 'store/store';

let cachedBlocks: Blocks;

function updateCachedBlocks(
  blocks: Blocks,
  types: string[] = ['AssessmentQuestion', 'AssessmentQuestionOptions']
) {
  //
  if (cachedBlocks) {
    // console.log('cachedBlocks has been updated. returning cachedBlocks');
    return cachedBlocks;
  }

  cachedBlocks = Object.values(blocks).reduce((acc, curr) => {
    const block = types.includes(curr?.type);

    return {
      ...acc,

      ...(block && { [curr.id]: curr }),
    };
  }, {});

  return cachedBlocks as Blocks;
}

export const resultsKeyMapToSuspendData = {
  answer: 0,
  attemptCount: 1,
  correctAnswer: 2,
  id: 3,
  isCorrect: 4,
  isLocked: 5,
  navigateToResults: 6,
  questionName: 7,
  submitButtonLabel: 8,
};

export const resultsKeyMapFromSuspendData = {
  0: 'answer',
  1: 'attemptCount',
  2: 'correctAnswer',
  3: 'id',
  4: 'isCorrect',
  5: 'isLocked',
  6: 'navigateToResults',
  7: 'questionName',
  8: 'submitButtonLabel',
};

function getResultsMinified(results: QuestionData, blocks: Blocks) {
  const resultsMinified = Object.values(results).reduce(
    (accA, currA, index) => {
      const keysRemapped = Object.keys(currA).reduce((accB, currB, index) => {
        return {
          ...accB,
          [resultsKeyMapToSuspendData[currB]]: currA[currB],
        };
      }, {});

      // this feels kinda clumsy
      // but isolates the work we want to do on a given entry
      //
      for (const key in keysRemapped) {
        if (keysRemapped[key] === true) {
          keysRemapped[key] = 1;
        }

        if (keysRemapped[key] === false) {
          keysRemapped[key] = 0;
        }

        if (key === '7') {
          keysRemapped[key] = '';
        }

        if (key === '8') {
          keysRemapped[key] =
            keysRemapped[key]?.toLowerCase() === 'submit' ? 1 : 0;
        }

        const questionBlock = blocks[Object.keys(results)[index]];

        let questionBlockOptions:
          | ''
          | undefined
          | Block
          | Block[] = (questionBlock?.children as Block[])?.find(child => {
          const block = blocks[child.id];
          return block?.type === 'AssessmentQuestionOptions';
        });

        //

        const questionBlockOptionsId = questionBlockOptions?.id;
        questionBlockOptions =
          questionBlockOptionsId && blocks[questionBlockOptionsId];

        // these could be DRY'd up. leaving for the moment
        if (key === '0') {
          keysRemapped['0'] = (keysRemapped['0'] || []).map(entry => {
            return ((questionBlockOptions as Block)?.children as Block[])
              ?.map(child => child.id)
              .indexOf(entry);
          });
        }

        if (key === '2') {
          keysRemapped['2'] = (keysRemapped['2'] || []).map(entry => {
            return ((questionBlockOptions as Block)?.children as Block[])
              ?.map(child => child.id)
              .indexOf(entry);
          });
        }
        //
      }

      return {
        ...accA,
        // was object
        // [currA.id]: {
        //   ...keysRemapped,
        // },
        //now array
        [currA.id]: Object.values(keysRemapped),
      };
    },
    {}
  );

  return resultsMinified;
}

function getResultsMaxified(results: QuestionData, blocks: Blocks) {
  const resultsMaxified = Object.values(results).reduce(
    (accA, currA, index) => {
      const keysRemapped = Object.keys(currA).reduce((accB, currB) => {
        return {
          ...accB,
          [resultsKeyMapFromSuspendData[currB]]: currA[currB],
        };
      }, {});

      // this feels kinda clumsy
      // but isolates the work we want to do on a given entry
      //
      for (const key in keysRemapped) {
        if (['isCorrect', 'isLocked', 'navigateToResults'].includes(key)) {
          if (keysRemapped[key] === 1) {
            keysRemapped[key] = true;
          }

          if (keysRemapped[key] === 0) {
            keysRemapped[key] = false;
          }
        }

        if (key === 'submitButtonLabel') {
          keysRemapped['submitButtonLabel'] = keysRemapped[
            'submitButtonLabel'
          ].toString();
        }

        const sId = Object.keys(results)[index];

        const questionBlock = Object.values(blocks).find(b => {
          return (
            b?.type === 'AssessmentQuestion' && b?.props?.sId === Number(sId)
          );
        });

        // my type garbage. needs fixing
        let questionBlockOptions:
          | ''
          | undefined
          | Block
          | Block[] = (questionBlock?.children as Block[])?.find(child => {
          const block = blocks[child.id];
          return block?.type === 'AssessmentQuestionOptions';
        });

        const questionBlockOptionsId = questionBlockOptions?.id;
        questionBlockOptions =
          questionBlockOptionsId && blocks[questionBlockOptionsId];

        if (key === 'answer' && questionBlockOptions) {
          keysRemapped['answer'] = (keysRemapped['answer'] || []).map(entry => {
            return ((questionBlockOptions as Block)?.children as Block[])[entry]
              ?.id;
          });
        }

        if (key === 'correctAnswer' && questionBlockOptions) {
          keysRemapped['correctAnswer'] = (
            keysRemapped['correctAnswer'] || []
          ).map(entry => {
            return ((questionBlockOptions as Block)?.children as Block[])[entry]
              ?.id;
          });
        }
      }

      return {
        ...accA,
        ...(currA?.[3] && {
          [currA[3]]: {
            ...keysRemapped,
          },
        }),
      };
    },
    {}
  );

  return resultsMaxified;
}

function transformAssessmentsToSuspendData(
  assessments: Assessments,
  state: State = {} as State
) {
  const assessmentsMinified = Object.keys(assessments || {}).reduce(
    (acc, key) => {
      const assessment = assessments[key];

      const sId =
        state.topicsIdSIdMapping.idToSId[key] || state.blocks[key]?.props?.sId;
      const assessmentSId = String(sId) || key;
      //

      const { data, results } = structuredClone(assessment);

      const resultsUpdatingIdToSId = {
        ...results,
      };

      //
      Object.keys((resultsUpdatingIdToSId || {}) as any).forEach(k => {
        if (state?.blocks) {
          //
          const sId =
            state.assessmentQuestionIdSIdMapping.idToSId[k] ||
            state.blocks[k]?.props?.sId;
          if (sId) {
            resultsUpdatingIdToSId[k]['id'] = sId;
          }
          //
        }
      });

      const resultsMinified = getResultsMinified(
        resultsUpdatingIdToSId as QuestionData,
        updateCachedBlocks(state.blocks)
      );

      const assessmentMinified = {
        d: {
          ...data,
        },
        r: {
          ...resultsMinified,
        },
      };

      return {
        ...acc,
        [assessmentSId]: assessmentMinified,
      };
    },
    {}
  );

  // console.log(
  //   'assessments minified bytes size ?',
  //   JSON.stringify(assessmentsMinified).length
  // );

  // const assessmentsBytes = JSON.stringify(assessments).length;
  // const assessmentsMinifiedBytes = JSON.stringify(assessmentsMinified).length;
  // const savings =
  //   ((assessmentsBytes - assessmentsMinifiedBytes) / assessmentsBytes) * 100;

  // console.log('% savings :', savings.toFixed());

  return assessmentsMinified;
}

function transformAssessmentsFromSuspendData(
  assessments: Assessments,
  state: State = {} as State
) {
  return Object.keys(assessments || {}).reduce((acc, key) => {
    const assessment = assessments[key];
    const assessmentId =
      state.topicsIdSIdMapping.sIdToId[key] ||
      Object.values(state.blocks).find(block => {
        return block?.props?.sId === Number(key);
      })?.id ||
      ''; // if we're undefined here. problems

    const { d, r } = assessment;
    const resultsUpdatingSIdToId = {
      ...r,
    } as QuestionData;

    //
    Object.values(resultsUpdatingSIdToId || {}).forEach((entry: any) => {
      if (state?.blocks && entry?.['3']) {
        const id =
          state.assessmentQuestionIdSIdMapping.sIdToId[entry['3']] ||
          Object.values(state.blocks).find(block => {
            return block?.props?.sId === Number(entry['3']);
          })?.id;

        if (id) {
          entry['3'] = id;
        }
        //
      }
    });

    const resultsMaxified = getResultsMaxified(
      resultsUpdatingSIdToId as QuestionData,
      updateCachedBlocks(state.blocks)
    );

    const assessmentMaxified = {
      data: {
        ...d,
      },
      results: {
        ...resultsMaxified,
      },
    };

    return {
      ...acc,
      ...(assessmentId && {
        [assessmentId]: assessmentMaxified,
      }),
    };
  }, {});
}

export {
  transformAssessmentsToSuspendData,
  transformAssessmentsFromSuspendData,
};
